import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Check, ArrowRight, Sun, Moon } from "lucide-react";
import { MarkBtb } from "@/components/BrandLogos";
import { API } from "@/lib/api";
import { useDocumentMeta } from "@/lib/meta";

const COMPLIANCE = [
  "Replace the compliance retainer you can't actually see into — inspect it yourself.",
  "Live policies, not dead PDFs. One version in force; every change audit-trailed.",
  "Horizon scans of SRA, Lexcel & Law Society → your directors approve. Nothing changes silently.",
  "Firm-wide risk assessment in one touch — always ready, never rebuilt in a panic.",
];

const CASE = [
  "Built once, around how you actually work. You own it — no per-seat rent.",
  "Your data stays in your own Microsoft 365. No black box, no ransom to leave.",
  "A workflow built per department; one firm-wide view for owners.",
  "Director's Lounge: pipeline, risk, deadlines and performance — live.",
];

const THEMES = {
  light: {
    page: "#eceae6",
    card: "#faf9f7",
    heading: "#14110f",
    body: "#4a453f",
    bodyStrong: "#2b2622",
    muted: "#6b6560",
    hairline: "#e2ded8",
    accent: "#752023",
    band: "#14110f",
    bandEyebrow: "#e0a3a5",
    bandBody: "#ffffff",
    cta: "#752023",
    logo: "#14110f",
  },
  dark: {
    page: "#0a0908",
    card: "#14110f",
    heading: "#f7f5f2",
    body: "#cfc9c2",
    bodyStrong: "#e8e4de",
    muted: "#8a847c",
    hairline: "#2e2a26",
    accent: "#d98f91",
    band: "#f7f5f2",
    bandEyebrow: "#752023",
    bandBody: "#14110f",
    cta: "#a33b3e",
    logo: "#f7f5f2",
  },
};

function Bullets({ items, testid, t }) {
  return (
    <ul className="mt-5 space-y-3" data-testid={testid}>
      {items.map((x, i) => (
        <li key={i} className="flex items-start gap-3">
          <Check strokeWidth={2.5} className="mt-[3px] h-4 w-4 shrink-0" style={{ color: t.accent }} />
          <span className="text-[15px] leading-snug" style={{ color: t.bodyStrong }}>
            {x}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function OnePager() {
  useDocumentMeta({
    title: "One-pager — ComplianceMinder & CaseMinder",
    description:
      "The owner's case for ComplianceMinder and CaseMinder — own your compliance and operations on the Microsoft 365 you already control.",
    path: "/one-pager",
  });

  const cardRef = useRef(null);
  const [dark, setDark] = useState(
    () => new URLSearchParams(window.location.search).get("theme") === "dark"
  );
  const t = dark ? THEMES.dark : THEMES.light;

  const eyebrow = "text-[0.62rem] font-bold uppercase tracking-[0.16em]";

  return (
    <div className="min-h-screen w-full py-10" style={{ background: t.page }} data-testid="one-pager-page">
      {/* Action bar (excluded from export + print) */}
      <div className="no-print mx-auto mb-8 flex w-full max-w-[860px] flex-wrap items-center justify-between gap-4 px-6">
        <Link
          to="/"
          data-testid="onepager-home"
          className="text-xs font-semibold uppercase tracking-[0.14em]"
          style={{ color: t.muted }}
        >
          ← Beyond the Box
        </Link>
        <div className="flex items-center gap-3">
          {/* Theme toggle */}
          <div className="flex items-center border" style={{ borderColor: dark ? "#3a352f" : "#c9c4bc" }}>
            <button
              type="button"
              data-testid="onepager-theme-light"
              onClick={() => setDark(false)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition-colors"
              style={{ background: !dark ? "#14110f" : "transparent", color: !dark ? "#fff" : t.muted }}
            >
              <Sun strokeWidth={2} className="h-3.5 w-3.5" /> Light
            </button>
            <button
              type="button"
              data-testid="onepager-theme-dark"
              onClick={() => setDark(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition-colors"
              style={{ background: dark ? "#f7f5f2" : "transparent", color: dark ? "#14110f" : t.muted }}
            >
              <Moon strokeWidth={2} className="h-3.5 w-3.5" /> Dark
            </button>
          </div>
          <a
            href={`${API}/one-pager/${dark ? "dark" : "light"}.png`}
            data-testid="onepager-download-png"
            className="inline-flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-90"
            style={{ background: "#752023" }}
          >
            <Download strokeWidth={2} className="h-4 w-4" /> Download image
          </a>
          <a
            href={`${API}/one-pager/${dark ? "dark" : "light"}.pdf`}
            data-testid="onepager-download-pdf"
            className="inline-flex items-center gap-2 border px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors"
            style={{ borderColor: dark ? "#3a352f" : "#c9c4bc", color: t.heading }}
          >
            <FileText strokeWidth={2} className="h-4 w-4" /> Download PDF
          </a>
        </div>
      </div>

      {/* Shareable card */}
      <div
        ref={cardRef}
        id="onepager-card"
        data-testid="onepager-card"
        className="mx-auto w-full max-w-[860px] px-10 py-11"
        style={{ background: t.card, fontFamily: "'IBM Plex Sans', sans-serif", color: t.heading }}
      >
        {/* Masthead */}
        <div className="flex items-center justify-between border-b pb-6" style={{ borderColor: t.hairline }}>
          <div className="flex items-center gap-3" style={{ color: t.logo }}>
            <MarkBtb className="h-9 w-9" strokeWidth={2.25} />
            <span
              className="text-[0.72rem] font-bold uppercase leading-[1.02] tracking-[0.06em]"
              style={{ fontFamily: "'Archivo', sans-serif" }}
            >
              <span className="block">Beyond</span>
              <span className="block">the</span>
              <span className="block">Box</span>
            </span>
          </div>
          <span className={eyebrow} style={{ color: t.accent }}>
            For owner-managed law firms
          </span>
        </div>

        {/* Headline */}
        <div className="pt-8">
          <h1
            className="text-[2.6rem] font-extrabold leading-[0.95] tracking-[-0.03em]"
            style={{ fontFamily: "'Archivo', sans-serif", color: t.heading }}
          >
            Stop renting your firm from software vendors.
          </h1>
          <p className="mt-5 max-w-[46rem] text-[17px] leading-relaxed" style={{ color: t.body }}>
            Own your compliance. Grow into your operations. On the Microsoft 365 you already pay for —
            no black box, no lock-in, no data held to ransom.
          </p>
        </div>

        {/* Two products */}
        <div className="mt-9 grid grid-cols-1 gap-8 border-t pt-9 md:grid-cols-2" style={{ borderColor: t.hairline }}>
          <div>
            <span className={eyebrow} style={{ color: t.accent }}>
              Start here
            </span>
            <h2 className="mt-2 text-2xl font-bold tracking-[-0.02em]" style={{ fontFamily: "'Archivo', sans-serif", color: t.heading }}>
              ComplianceMinder
            </h2>
            <p className="mt-1 text-[13px] font-medium" style={{ color: t.muted }}>
              Own your compliance
            </p>
            <Bullets items={COMPLIANCE} testid="onepager-compliance-bullets" t={t} />
          </div>
          <div className="md:border-l md:pl-8" style={{ borderColor: t.hairline }}>
            <span className={eyebrow} style={{ color: t.accent }}>
              Grow into it
            </span>
            <h2 className="mt-2 text-2xl font-bold tracking-[-0.02em]" style={{ fontFamily: "'Archivo', sans-serif", color: t.heading }}>
              CaseMinder
            </h2>
            <p className="mt-1 text-[13px] font-medium" style={{ color: t.muted }}>
              Own your operations
            </p>
            <Bullets items={CASE} testid="onepager-case-bullets" t={t} />
          </div>
        </div>

        {/* Symbiosis band */}
        <div className="mt-9 p-6" style={{ background: t.band }}>
          <span className={eyebrow} style={{ color: t.bandEyebrow }}>
            Run them together
          </span>
          <p className="mt-3 text-[16px] leading-snug" style={{ color: t.bandBody }}>
            A policy change flows straight into how the firm works — drafted, director-approved, built
            into the workflow. Compliance that changes behaviour, not a folder no one reads.
          </p>
        </div>

        {/* M365 trust — for IT */}
        <div className="mt-8 border-t pt-6" style={{ borderColor: t.hairline }}>
          <span className={eyebrow} style={{ color: t.accent }}>
            Send this to your IT team
          </span>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: t.body }}>
            Runs in <strong style={{ color: t.heading }}>your own Microsoft 365 tenant</strong> · your data never
            leaves your estate · every Microsoft Graph permission explained · auditable by your own IT team.
            Ask us for the playbook.
          </p>
        </div>

        {/* Proof + CTA */}
        <div className="mt-8 flex flex-col gap-6 border-t pt-7 md:flex-row md:items-end md:justify-between" style={{ borderColor: t.hairline }}>
          <p className="max-w-[26rem] text-[13px] leading-snug" style={{ color: t.muted }}>
            Built and proven inside a working law firm — <strong style={{ color: t.heading }}>Bonsai Law</strong> ran
            it first, and remains the escalation route for regulated legal advice.
          </p>
          <div className="md:text-right">
            <div
              className="inline-flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white"
              style={{ background: t.cta }}
            >
              Book a walkthrough <ArrowRight strokeWidth={2} className="h-4 w-4" />
            </div>
            <p className="mt-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em]" style={{ color: t.muted }}>
              beyondthebox.law
            </p>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 12mm; }
          [data-testid="one-pager-page"] { background: #ffffff !important; padding: 0 !important; }
          #onepager-card { max-width: 100% !important; box-shadow: none !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
